import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export type NotificationScope = 'broadcast' | 'users' | 'user';
export type NotificationType = 'system' | 'offer';

export interface SendBroadcastPayload {
  scope: 'broadcast';
  title?: string;
  message: string;
  type: NotificationType;
  app_id: string; // uuid
}

export interface SendUsersPayload {
  scope: 'users';
  user_ids: number[];
  title?: string;
  message: string;
  type: NotificationType;
  data?: Record<string, unknown>;
}

export interface SendUserPayload {
  scope: 'user';
  user_id: number;
  title?: string;
  message: string;
  type: NotificationType;
}

export type SendNotificationPayload = SendBroadcastPayload | SendUsersPayload | SendUserPayload;

export interface SendNotificationResponse {
  success: boolean;
  created_ids: number[];
}

export const sendNotification = createAsyncThunk<SendNotificationResponse, SendNotificationPayload, { rejectValue: string }>(
  'notifications/send',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post('/admin/notifications/', payload);
      return res.data as SendNotificationResponse;
    } catch (e: any) {
      const message = e?.response?.data?.error || e?.response?.data?.detail || e.message || 'Failed to send notification';
      return rejectWithValue(message);
    }
  }
);

interface NotificationsState {
  sending: boolean;
  error: string | null;
  lastResult: SendNotificationResponse | null;
}

const initialState: NotificationsState = {
  sending: false,
  error: null,
  lastResult: null,
};

const slice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationResult(state) {
      state.lastResult = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendNotification.pending, (state) => {
        state.sending = true;
        state.error = null;
        state.lastResult = null;
      })
      .addCase(sendNotification.fulfilled, (state, action) => {
        state.sending = false;
        state.lastResult = action.payload;
      })
      .addCase(sendNotification.rejected, (state, action) => {
        state.sending = false;
        state.error = action.payload || 'Failed to send notification';
      });
  },
});

export const { clearNotificationResult } = slice.actions;
export default slice.reducer;


